import {
  BackSide,
  Mesh,
  ShaderMaterial,
  SphereGeometry,
  Uniform,
} from "three";
import {
  DEFAULT_UNDERWATER_VISUAL_SETTINGS,
  type UnderwaterVisualSettings,
} from "./VisualSettings";

const vertexShader = /* glsl */ `
  varying vec3 vLocalPosition;

  void main() {
    vLocalPosition = position;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const fragmentShader = /* glsl */ `
  uniform float opacity;
  uniform float topIntensity;
  uniform float bottomIntensity;
  uniform float heightOffset;
  uniform float radius;
  varying vec3 vLocalPosition;

  void main() {
    float y = clamp((vLocalPosition.y + radius + heightOffset) / (radius * 2.0), 0.0, 1.0);
    float top = smoothstep(0.38, 1.0, y);
    float bottom = 1.0 - smoothstep(0.0, 0.62, y);
    float middle = 1.0 - abs(y - 0.5) * 2.0;

    vec3 bottomColor = vec3(0.0, 0.18, 0.3) * bottomIntensity;
    vec3 middleColor = vec3(0.0, 0.46, 0.58) * (0.74 + middle * 0.2);
    vec3 topColor = vec3(0.18, 0.86, 0.95) * topIntensity;
    vec3 color = bottomColor * bottom + middleColor * (1.0 - bottom * 0.45) + topColor * top * 0.55;

    gl_FragColor = vec4(color, opacity);
  }
`;

export class WaterGradientBackdrop extends Mesh {
  private readonly opacityUniform: Uniform<number>;
  private readonly topIntensityUniform: Uniform<number>;
  private readonly bottomIntensityUniform: Uniform<number>;
  private readonly heightUniform: Uniform<number>;

  constructor(roomSize: number, roomHeight: number) {
    const radius = Math.max(roomSize, roomHeight) * 1.65;
    const geometry = new SphereGeometry(radius, 48, 24);
    const material = new ShaderMaterial({
      transparent: true,
      depthWrite: false,
      depthTest: false,
      side: BackSide,
      uniforms: {
        opacity: new Uniform(
          DEFAULT_UNDERWATER_VISUAL_SETTINGS.waterGradientOpacity
        ),
        topIntensity: new Uniform(
          DEFAULT_UNDERWATER_VISUAL_SETTINGS.waterGradientTopIntensity
        ),
        bottomIntensity: new Uniform(
          DEFAULT_UNDERWATER_VISUAL_SETTINGS.waterGradientBottomIntensity
        ),
        heightOffset: new Uniform(
          DEFAULT_UNDERWATER_VISUAL_SETTINGS.waterGradientHeight
        ),
        radius: new Uniform(radius),
      },
      vertexShader,
      fragmentShader,
    });

    super(geometry, material);

    this.opacityUniform = material.uniforms.opacity as Uniform<number>;
    this.topIntensityUniform = material.uniforms.topIntensity as Uniform<number>;
    this.bottomIntensityUniform = material.uniforms
      .bottomIntensity as Uniform<number>;
    this.heightUniform = material.uniforms.heightOffset as Uniform<number>;
    this.name = "water-gradient-backdrop";
    this.renderOrder = -1000;
  }

  setSettings(settings: UnderwaterVisualSettings) {
    this.visible = settings.waterGradientOpacity > 0;
    this.opacityUniform.value = settings.waterGradientOpacity;
    this.topIntensityUniform.value = settings.waterGradientTopIntensity;
    this.bottomIntensityUniform.value = settings.waterGradientBottomIntensity;
    this.heightUniform.value = settings.waterGradientHeight;
  }
}
